import { configPrice } from "./modules/Price";

export interface FormatNumberParams {
  prefix?: string;
  suffix?: string;
  reset?: boolean;
  currency?: string;
  locales?: string;
  minDecimal?: number;
  maxDecimal?: number;
}

const escapeRegExp = (char: string): string =>
  char.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const getLocaleSeparators = (locale: string) => {
  const parts = new Intl.NumberFormat(locale).formatToParts(1111.1);
  const group = parts.find((p) => p.type === "group")?.value || ".";
  const decimal = parts.find((p) => p.type === "decimal")?.value || ",";
  return { group, decimal };
};

const formatNumber = (
  value: number | string,
  params?: FormatNumberParams
): string | number => {
  if (value === undefined || value === null || value === "") return "";

  const currencyCode = (params?.currency || "IDR").toUpperCase();
  const config = configPrice[currencyCode];
  const isIdr = currencyCode === "IDR";

  // Resolusi locale: params.locales -> configPrice[currencyCode].locales -> fallback "id-ID"
  const locale = params?.locales || config?.locales || "id-ID";

  const parseString = (): number => {
    const { group, decimal } = getLocaleSeparators(locale);
    const escapedGroup = escapeRegExp(group);
    const escapedDecimal = escapeRegExp(decimal);
    const regexValidChars = new RegExp(`[^0-9${escapedGroup}${escapedDecimal}]`, "g");
    const cleanStr = String(value).replace(regexValidChars, "");

    const normalized = cleanStr
      .replace(new RegExp(escapedGroup, "g"), "")
      .replace(new RegExp(escapedDecimal, "g"), ".");

    return Number(normalized);
  };

  const parseNumber = (): string => {
    const defaultMinFraction = isIdr ? (String(value).includes(".") ? 2 : 0) : 2;
    const defaultMaxFraction = 2;

    const formatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits: params?.minDecimal !== undefined ? params.minDecimal : defaultMinFraction,
      maximumFractionDigits: params?.maxDecimal !== undefined ? params.maxDecimal : defaultMaxFraction,
      numberingSystem: "latn"
    } as Intl.NumberFormatOptions);

    const formattedValue = formatter.format(Number(value));

    if (params?.prefix && params?.suffix) {
      return `${params.prefix} ${formattedValue} ${params.suffix}`;
    } else if (params?.prefix) {
      return `${params.prefix} ${formattedValue}`;
    } else if (params?.suffix) {
      return `${formattedValue} ${params.suffix}`;
    }
    return formattedValue;
  };

  if (typeof value === "number") {
    return parseNumber();
  } else if (typeof value === "string") {
    if (params?.reset) {
      return parseString();
    }
    return formatNumber(parseString(), params);
  }
  return value;
};

export default formatNumber;
